import React from 'react';
import { EDGE_DENSITY } from './graphConfig';
import { cn } from '@/lib/utils';

const DENSITY_KEYS = Object.entries(EDGE_DENSITY);

interface LegendItem {
  key?: string;
  label: string;
  color: string;
}

interface CenterItem {
  color: string;
  label: string;
}

interface GraphLegendProps {
  title: string;
  items?: LegendItem[];
  centerItem?: CenterItem | null;
  minEdgeValue?: number;
  maxEdgeValue?: number;
  onMinEdgeValueChange?: (value: number) => void;
  edgePercent?: number;
  onEdgePercentChange?: (value: number) => void;
  edgeDensity?: number;
  onEdgeDensityChange?: (value: number) => void;
  showEdgeDensity?: boolean;
  secondSliderValue?: number;
  secondSliderMin?: number;
  secondSliderMax?: number;
  onSecondSliderChange?: (value: number) => void;
  secondSliderLabel?: string;
  onItemClick?: (key: string) => void;
  selectedItems?: Set<string>;
  filterLabel?: string;
  t?: (key: string) => string;
  children?: React.ReactNode;
}

const sliderClass =
  'w-full h-1 cursor-pointer appearance-none bg-muted rounded-full accent-primary';

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
      {children}
    </span>
  );
}

export default function GraphLegend({
  title,
  items = [],
  centerItem = null,
  minEdgeValue,
  maxEdgeValue,
  onMinEdgeValueChange,
  edgePercent,
  onEdgePercentChange,
  edgeDensity,
  onEdgeDensityChange,
  showEdgeDensity = false,
  secondSliderValue,
  secondSliderMin,
  secondSliderMax,
  onSecondSliderChange,
  secondSliderLabel,
  onItemClick,
  selectedItems,
  filterLabel,
  t,
  children,
}: GraphLegendProps) {
  const hasFilter = selectedItems && selectedItems.size > 0;

  return (
    <div className="absolute top-4 right-4 z-[100] w-[240px] max-h-[calc(100%-32px)] flex flex-col overflow-hidden bg-background border border-ctu-border rounded-md shadow-sm">
      {/* Title */}
      <div className="px-4 pt-4 pb-3 shrink-0 border-b border-ctu-border">
        <Eyebrow>{title}</Eyebrow>
      </div>

      {/* Edge visibility percentage slider */}
      {onEdgePercentChange && (
        <div className="px-4 py-3 shrink-0 border-b border-ctu-border">
          <div className="flex items-baseline justify-between mb-2">
            <Eyebrow>{filterLabel || 'Edge visibility'}</Eyebrow>
            <span className="font-mono tabular-nums text-xs text-ctu-text">{edgePercent}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            value={edgePercent}
            onChange={(e) => onEdgePercentChange(Number(e.target.value))}
            className={sliderClass}
          />
        </div>
      )}

      {/* Edge density toggle */}
      {showEdgeDensity && onEdgeDensityChange && (
        <div className="px-4 py-3 shrink-0 border-b border-ctu-border">
          <div className="mb-2">
            <Eyebrow>{t?.('filter.edgeDensity') || 'Edge density'}</Eyebrow>
          </div>
          <div className="flex gap-1 flex-wrap">
            {DENSITY_KEYS.map(([key, value]) => (
              <button
                key={key}
                onClick={() => onEdgeDensityChange(value)}
                className={cn(
                  'px-2 py-0.5 text-[10px] rounded cursor-pointer transition-colors border font-mono tabular-nums',
                  edgeDensity === value
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-ctu-border bg-transparent text-ctu-text-muted hover:text-ctu-text',
                )}
              >
                {t?.(`filter.density.${key.toLowerCase()}`) || `${Math.round(value * 100)}%`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Min edge value slider */}
      {maxEdgeValue !== undefined && maxEdgeValue > 1 && onMinEdgeValueChange && (
        <div className="px-4 py-3 shrink-0 border-b border-ctu-border">
          <div className="flex items-baseline justify-between mb-2">
            <Eyebrow>{filterLabel || 'Min. connections'}</Eyebrow>
            <span className="font-mono tabular-nums text-xs text-ctu-text">{minEdgeValue}</span>
          </div>
          <input
            type="range"
            min={1}
            max={maxEdgeValue}
            value={minEdgeValue}
            onChange={(e) => onMinEdgeValueChange(Number(e.target.value))}
            className={sliderClass}
          />
          <div className="flex justify-between text-[10px] font-mono tabular-nums text-ctu-text-muted mt-1">
            <span>1</span>
            <span>{maxEdgeValue}</span>
          </div>
        </div>
      )}

      {/* Second slider (e.g. node count) */}
      {onSecondSliderChange && secondSliderMax !== undefined && secondSliderMin !== undefined && secondSliderMax > secondSliderMin && (
        <div className="px-4 py-3 shrink-0 border-b border-ctu-border">
          <div className="flex items-baseline justify-between mb-2">
            <Eyebrow>{secondSliderLabel || 'Count'}</Eyebrow>
            <span className="font-mono tabular-nums text-xs text-ctu-text">{secondSliderValue}</span>
          </div>
          <input
            type="range"
            min={secondSliderMin}
            max={secondSliderMax}
            value={secondSliderValue}
            onChange={(e) => onSecondSliderChange(Number(e.target.value))}
            className={sliderClass}
          />
          <div className="flex justify-between text-[10px] font-mono tabular-nums text-ctu-text-muted mt-1">
            <span>{secondSliderMin}</span>
            <span>{secondSliderMax}</span>
          </div>
        </div>
      )}

      {/* Scrollable section: items + children */}
      <div className="overflow-y-auto flex-1 min-h-0 px-4 py-3">
        {/* Center item (for collaboration graph) */}
        {centerItem && (
          <div className="flex items-center gap-2 mb-1.5 text-xs text-ctu-text">
            <div
              className="size-2.5 rounded-full shrink-0"
              style={{ backgroundColor: centerItem.color }}
            />
            <span>{centerItem.label}</span>
          </div>
        )}

        {/* Legend items */}
        {items.map((item) => {
          const key = item.key || item.label;
          const isSelected = selectedItems?.has(key);
          const dimmed = hasFilter && !isSelected;
          return (
            <div
              key={key}
              onClick={onItemClick ? () => onItemClick(key) : undefined}
              className={cn(
                'flex items-center gap-2 mb-1 text-xs rounded-md transition-all duration-150 text-ctu-text',
                onItemClick ? 'cursor-pointer px-1.5 py-0.5 hover:bg-muted/60' : 'cursor-default',
                dimmed && 'opacity-40',
              )}
              style={{
                backgroundColor: isSelected ? `${item.color}18` : 'transparent',
              }}
            >
              <div
                className="size-2.5 rounded-full shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <span className="truncate">{item.label}</span>
            </div>
          );
        })}

        {/* Extra content (e.g. reset layout button) */}
        {children}
      </div>
    </div>
  );
}
